/**
 * ViewportScaling - Makes SVG content truly responsive like a viewport
 * 
 * This system ensures content SCALES and REPOSITIONS based on container size,
 * not just changes container dimensions.
 */

export interface ViewportConfig {
  // Target container size
  containerWidth: number;
  containerHeight: number;
  
  // Reference size (what the content was designed for)
  referenceWidth: number;
  referenceHeight: number;
  
  // Scaling strategy
  scalingMode: 'proportional' | 'responsive' | 'fluid';
  
  // Breakpoints for layout changes
  breakpoint?: 'mobile' | 'tablet' | 'desktop' | 'wide';
}

export interface ScaledElement {
  x: number;
  y: number;
  width: number;
  height: number;
  fontSize?: number;
  strokeWidth?: number;
}

export class ViewportScaling {
  // Reference dimensions (what content is designed for)
  static readonly REFERENCE = {
    width: 700,
    height: 480,
    fontSize: {
      h1: 22,
      h2: 16,
      body: 12,
      small: 10
    }
  };
  
  // Breakpoints for responsive behavior
  static readonly BREAKPOINTS = {
    mobile: { max: 400, layout: 'stacked' },
    tablet: { max: 600, layout: 'compact' },
    desktop: { max: 1200, layout: 'normal' },
    wide: { max: Infinity, layout: 'expanded' }
  };
  
  /**
   * Calculate scale factors for current viewport
   */
  static getScaleFactors(config: ViewportConfig): { scaleX: number; scaleY: number; scale: number } {
    const scaleX = config.containerWidth / config.referenceWidth;
    const scaleY = config.containerHeight / config.referenceHeight;
    
    // Uniform scaling (maintain aspect ratio)
    const scale = Math.min(scaleX, scaleY);
    
    return { scaleX, scaleY, scale };
  }
  
  /**
   * Scale an element's position and size
   */
  static scaleElement(
    element: ScaledElement,
    config: ViewportConfig
  ): ScaledElement {
    const { scale } = this.getScaleFactors(config);
    
    if (config.scalingMode === 'proportional') {
      // Everything scales uniformly
      return {
        x: element.x * scale,
        y: element.y * scale,
        width: element.width * scale,
        height: element.height * scale,
        fontSize: element.fontSize ? element.fontSize * scale : undefined,
        strokeWidth: element.strokeWidth ? element.strokeWidth * scale : undefined
      };
    } else if (config.scalingMode === 'responsive') {
      // Smart scaling based on breakpoints
      const breakpoint = this.getBreakpoint(config.containerWidth);
      return this.applyResponsiveScaling(element, config, breakpoint);
    } else {
      // Fluid - width stretches, height maintains proportions
      return this.applyFluidScaling(element, config);
    }
  }
  
  /**
   * Get current breakpoint
   */
  static getBreakpoint(width: number): 'mobile' | 'tablet' | 'desktop' | 'wide' {
    if (width <= 400) return 'mobile';
    if (width <= 600) return 'tablet';
    if (width <= 1200) return 'desktop';
    return 'wide';
  }
  
  /**
   * Apply responsive scaling based on breakpoint
   */
  private static applyResponsiveScaling(
    element: ScaledElement,
    config: ViewportConfig,
    breakpoint: 'mobile' | 'tablet' | 'desktop' | 'wide'
  ): ScaledElement {
    const { scaleX, scaleY } = this.getScaleFactors(config);
    
    switch (breakpoint) {
      case 'mobile':
        // Stack elements vertically, full width
        return {
          x: 20, // Fixed padding
          y: element.y * scaleY,
          width: config.containerWidth - 40, // Full width minus padding
          height: element.height * scaleY,
          fontSize: Math.max(10, (element.fontSize || 12) * 0.9), // Slightly smaller text
          strokeWidth: element.strokeWidth
        };
        
      case 'tablet':
        // Compact layout
        return {
          x: element.x * scaleX * 0.9, // Slightly tighter
          y: element.y * scaleY,
          width: element.width * scaleX * 0.9,
          height: element.height * scaleY,
          fontSize: element.fontSize ? element.fontSize * Math.min(scaleX, 1) : undefined,
          strokeWidth: element.strokeWidth
        };
        
      case 'desktop':
      case 'wide':
        // Normal/expanded layout
        return {
          x: element.x * scaleX,
          y: element.y * scaleY,
          width: element.width * scaleX,
          height: element.height * scaleY,
          fontSize: element.fontSize ? element.fontSize * Math.min(scaleX, 1.2) : undefined,
          strokeWidth: element.strokeWidth
        };
    }
  }
  
  /**
   * Apply fluid scaling (width stretches, height proportional)
   */
  private static applyFluidScaling(
    element: ScaledElement,
    config: ViewportConfig
  ): ScaledElement {
    const { scaleX, scaleY } = this.getScaleFactors(config);
    
    return {
      x: element.x * scaleX,
      y: element.y * scaleY,
      width: element.width * scaleX,
      height: element.height * scaleY,
      fontSize: element.fontSize ? Math.max(10, element.fontSize * Math.min(scaleX, 1)) : undefined,
      strokeWidth: element.strokeWidth ? Math.max(1, element.strokeWidth * Math.min(scaleX, 1)) : undefined
    };
  }
  
  /**
   * Get responsive layout for mortgage paths diagram
   */
  static getMortgagePathsLayout(width: number, height: number): any {
    const breakpoint = this.getBreakpoint(width);
    const scale = width / this.REFERENCE.width;
    
    if (breakpoint === 'mobile') {
      // STACKED LAYOUT for mobile
      return {
        topBoxes: {
          layout: 'vertical',
          x: 20,
          spacing: 10,
          width: width - 40,
          height: 60
        },
        junction: {
          hidden: true // Hide junction on mobile
        },
        bottomBoxes: {
          layout: 'vertical',
          x: 20,
          y: 280 * scale,
          spacing: 10,
          width: width - 40,
          height: 60
        }
      };
    } else if (breakpoint === 'tablet') {
      // COMPACT LAYOUT for tablet
      return {
        topBoxes: {
          layout: 'horizontal',
          startX: 30,
          spacing: (width - 60) / 4, // Tighter spacing
          width: Math.min(140, width / 5),
          height: 70
        },
        junction: {
          x: width / 2 - 40,
          y: 120,
          width: 80,
          height: 40
        },
        bottomBoxes: {
          layout: 'horizontal',
          leftX: 50,
          rightX: width - 200,
          width: 150,
          height: 70
        }
      };
    } else {
      // NORMAL/WIDE LAYOUT
      const scaledWidth = 160 * scale;
      const scaledHeight = 80 * scale;
      
      return {
        topBoxes: {
          layout: 'horizontal',
          startX: 60 * scale,
          spacing: 180 * scale,
          width: scaledWidth,
          height: scaledHeight
        },
        junction: {
          x: (width / 2) - (75 * scale),
          y: 120 * scale,
          width: 150 * scale,
          height: 50 * scale
        },
        bottomBoxes: {
          layout: 'horizontal',
          leftX: 120 * scale,
          rightX: width - (120 * scale) - scaledWidth,
          width: scaledWidth,
          height: scaledHeight * 0.9
        }
      };
    }
  }
  
  /**
   * Calculate text size based on container width
   */
  static getResponsiveFontSize(
    baseSize: number,
    containerWidth: number,
    minSize: number = 8,
    maxSize: number = 24
  ): number {
    const scale = containerWidth / this.REFERENCE.width;
    const scaled = baseSize * scale;
    
    // Clamp between min and max
    return Math.max(minSize, Math.min(maxSize, scaled));
  }
  
  /**
   * Get viewBox for proper SVG scaling
   */
  static getViewBox(config: ViewportConfig): string {
    if (config.scalingMode === 'proportional') {
      // Use reference dimensions, let SVG scale
      return `0 0 ${config.referenceWidth} ${config.referenceHeight}`;
    } else {
      // Use actual dimensions
      return `0 0 ${config.containerWidth} ${config.containerHeight}`;
    }
  }
  
  /**
   * Transform string for scaling content
   */
  static getTransform(config: ViewportConfig): string {
    const { scale } = this.getScaleFactors(config);
    
    if (config.scalingMode === 'proportional' && scale !== 1) {
      // Center the scaled content
      const translateX = (config.containerWidth - config.referenceWidth * scale) / 2;
      const translateY = (config.containerHeight - config.referenceHeight * scale) / 2;
      
      return `translate(${translateX}, ${translateY}) scale(${scale})`;
    }
    
    return '';
  }
}

export default ViewportScaling;